import React from "react";
import { supabase } from "../../supabaseClient";
import PayoutDetails from "./PayoutDetails";
import { calculateTotalAllocated } from "./payoutUtils";
import {
  payoutTotalsMatchFund,
  wholeDollarFundTarget,
} from "../../utility/generateShapedPayoutTable";

const EventPayoutCard = ({
  event,
  details,
  editMode,
  localEventDetails,
  setLocalEventDetails,
  unsavedChanges,
  setUnsavedChanges,
  user,
  firstEventDetails,
  refreshEventDetails,
}) => {
  const handlePayoutQuantityChange = async (eventId, value) => {
    try {
      const quantity = value === "default" ? null : parseInt(value);
      const details = localEventDetails[eventId];

      // Find the selected payout
      const selectedPayout = details.possiblePayouts.find(
        (p) => p.quantity === (quantity || details.defaultPlacesToPay)
      );

      if (selectedPayout) {
        const roundedPayouts = selectedPayout.payouts.map((payout) => ({
          ...payout,
          percentage:
            details.prizeFund > 0
              ? payout.amount / details.prizeFund
              : payout.percentage,
        }));

        // Delete existing payouts for this event
        const { error: deleteError } = await supabase
          .from("tournamentPayouts")
          .delete()
          .eq("eventID", eventId);

        if (deleteError) throw deleteError;

        // Insert new payouts
        const payoutsToInsert = roundedPayouts.map((payout) => ({
          eventID: eventId,
          place: payout.place,
          percentage: payout.percentage,
          amount: payout.amount,
          createdBy: user.id,
          updatedAt: new Date().toISOString(),
        }));

        const { error: insertError } = await supabase
          .from("tournamentPayouts")
          .insert(payoutsToInsert);

        if (insertError) throw insertError;

        // Update the payout ratio override
        const { error } = await supabase
          .from("tournamentEvents")
          .update({
            payoutRatioOverride:
              quantity === details.defaultPlacesToPay ? null : quantity,
          })
          .eq("id", eventId);

        if (error) throw error;

        // Update local state
        setLocalEventDetails((prev) => ({
          ...prev,
          [eventId]: {
            ...prev[eventId],
            currentPayoutQuantity: quantity || prev[eventId].defaultPlacesToPay,
            selectedPayout: {
              ...selectedPayout,
              payouts: roundedPayouts,
              isFromDatabase: true, // Mark as saved to database
            },
          },
        }));

        // Clear unsaved changes for this event
        setUnsavedChanges((prev) => {
          const newChanges = { ...prev };
          delete newChanges[eventId];
          return newChanges;
        });

        // Refresh event details to ensure everything is in sync
        if (refreshEventDetails) {
          await refreshEventDetails();
        }
      }
    } catch (error) {
      console.error("Error updating payout quantity:", error);
    }
  };

  const totalAllocated = details.selectedPayout
    ? calculateTotalAllocated(details.selectedPayout.payouts)
    : 0;
  const roundToWholeDollars = details.roundToWholeDollars !== false;
  const payableFund = roundToWholeDollars
    ? wholeDollarFundTarget(details.prizeFund)
    : details.prizeFund;
  const allocationOk = payoutTotalsMatchFund(
    totalAllocated,
    details.prizeFund,
    roundToWholeDollars
  );
  const allocationDifference =
    totalAllocated > (details.prizeFund || 0) + 0.01
      ? totalAllocated - (details.prizeFund || 0)
      : totalAllocated - payableFund;

  return (
    <div className="card-board p-4 h-full">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center flex-wrap">
          <h3 className="font-display font-bold text-lg text-brand-charcoal">
            {event.eventName}
          </h3>
          {event.isPrimary && (
            <span className="ml-2 inline-flex items-center bg-brand-charcoal text-brand-cream px-2 py-1 rounded-lg text-xs font-display font-bold">
              Primary
            </span>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <div className="grid grid-cols-3 gap-4 text-xs font-display font-bold tracking-wider uppercase text-brand-muted">
          <div className="text-center">Pay Down Places</div>
          <div className="text-center">
            Prize Fund
            <div className="mt-0.5 font-medium normal-case tracking-normal text-brand-muted">
              Board total
            </div>
          </div>
          <div className="text-center">Total Entries</div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            {editMode ? (
              <select
                value={details.currentPayoutQuantity || "default"}
                onChange={(e) =>
                  handlePayoutQuantityChange(event.id, e.target.value)
                }
                className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
              >
                <option value="default">
                  Tournament Default ({details.defaultPlacesToPay} Places)
                </option>
                {details.possiblePayouts.map((payout) => (
                  <option key={payout.quantity} value={payout.quantity}>
                    {payout.quantity} Places
                  </option>
                ))}
              </select>
            ) : (
              <div className="font-score text-brand-charcoal">
                {details.currentPayoutQuantity || details.defaultPlacesToPay}
              </div>
            )}
          </div>
          <div className="text-center font-score text-brand-charcoal">
            ${(details.prizeFund || 0).toFixed(2)}
            {roundToWholeDollars &&
            Math.abs((details.prizeFund || 0) - payableFund) > 0.01 ? (
              <p className="mt-1 text-xs normal-case tracking-normal text-brand-muted">
                Whole-dollar places total ${payableFund.toFixed(0)}.
              </p>
            ) : null}
          </div>
          <div className="text-center font-score text-brand-charcoal">
            {details.totalEntries}
          </div>
        </div>
      </div>

      {details.selectedPayout && details.selectedPayout.payouts && (
        <PayoutDetails
          selectedPayout={details.selectedPayout}
          editMode={editMode}
          eventId={event.id}
          allocationDifference={allocationDifference}
          allocationOk={allocationOk}
          totalAllocated={totalAllocated}
          localEventDetails={localEventDetails}
          setLocalEventDetails={setLocalEventDetails}
        />
      )}
    </div>
  );
};

export default EventPayoutCard;
